import type { ActivityPeriod, PeriodOption } from "@/shared/domain/actions";

import type { ActivityChartMode } from "./activityChartModes";
import styles from "./InvocationActivity.module.css";

const PERIOD_LABELS: Record<ActivityPeriod, string> = {
  "1m": "1 minute",
  "5m": "5 minutes",
  "15m": "15 minutes",
  "1h": "1 hour",
  "6h": "6 hours",
  "1d": "1 day",
  "1w": "1 week",
  "1mo": "1 month",
};

export interface ActivityChartFooterProps {
  mode: ActivityChartMode;
  /** The bucket width the server actually used; what the caption and "Auto" must agree with. */
  period: ActivityPeriod;
  /** What "Auto" resolves to for this window. */
  autoPeriod: ActivityPeriod;
  periodOptions: PeriodOption[];
  /** "auto" whenever no explicit width is in force, including one the window has since ruled out. */
  selected: ActivityPeriod | "auto";
  onSelect: (period: ActivityPeriod | "auto") => void;
}

/** How to read the chart, and the control for widening its buckets. */
export function ActivityChartFooter({
  mode,
  period,
  autoPeriod,
  periodOptions,
  selected,
  onSelect,
}: ActivityChartFooterProps) {
  return (
    <div className={styles.chartFooter}>
      <p className={styles.chartCaption}>
        {captionFor(mode, period)} Drag across the chart to zoom in.
      </p>
      <label className={styles.periodPicker}>
        Bucket
        <select
          value={selected}
          onChange={(event) =>
            onSelect(event.target.value as ActivityPeriod | "auto")
          }
        >
          <option value="auto">Auto ({PERIOD_LABELS[autoPeriod]})</option>
          {periodOptions.map((option) => (
            <option
              key={option.period}
              value={option.period}
              disabled={!option.allowed}
            >
              {PERIOD_LABELS[option.period]}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}

function captionFor(mode: ActivityChartMode, period: ActivityPeriod): string {
  const width = PERIOD_LABELS[period];
  switch (mode) {
    case "rollup":
    case "inflight":
      return `Each point is the peak over ${width}.`;
    case "outcomes":
      return `Each bar is what finished in ${width}.`;
    case "credits":
      return `Each bar is the processing units (RPU) consumed in ${width}.`;
  }
}
